import { useState } from "react";
import { isAxiosError } from "axios";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ConfirmModal from "../ConfirmModal";
import { openRoute } from "../../services/branch";
import {
  BranchErrorResponse,
  BranchReadinessItem,
  RouteNotReadyError,
  RouteReadiness,
} from "../../types/branch.type";
import {
  RouteOpenFailure,
  resolveItemStatus,
  resolveRouteMode,
  resolveRouteOpenFailure,
} from "./branchStatus";
import { ROUTE_ITEM_COLUMNS } from "./constants";
import ReadinessChip from "./ReadinessChip";

const EMPTY_CELL = "-";
/** รหัสสาย, ชื่อสาย and สถานะ — the columns before the per-route items. */
const LEADING_COLUMN_COUNT = 3;
const NOT_READY_HINT = "ยังขาดรายการบังคับของสาขาหรือของสายนี้";
const OPEN_ROUTE_DESCRIPTION =
  "สายจะออกจากโหมดทดสอบและเริ่มใช้งานจริง ระบบจะตรวจความพร้อมอีกครั้งก่อนเปิดใช้";

function ItemStatusCell({ item }: Readonly<{ item: BranchReadinessItem | undefined }>) {
  if (!item) return <>{EMPTY_CELL}</>;
  return <ReadinessChip view={resolveItemStatus(item)} />;
}

interface RouteRowProps {
  route: RouteReadiness;
  canOpenRoutes: boolean;
  onRequestOpen: (route: RouteReadiness) => void;
}

function RouteRow({ route, canOpenRoutes, onRequestOpen }: RouteRowProps) {
  const handleOpenClick = () => onRequestOpen(route);
  const itemByKey = new Map(route.items.map((item) => [item.key, item]));
  const openHint = route.is_ready ? "" : NOT_READY_HINT;

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {route.route_code}
        </Typography>
      </TableCell>
      <TableCell>{route.route_name}</TableCell>
      <TableCell align="center">
        <ReadinessChip view={resolveRouteMode(route)} />
      </TableCell>
      {ROUTE_ITEM_COLUMNS.map(({ key }) => (
        <TableCell key={key} align="center">
          <ItemStatusCell item={itemByKey.get(key)} />
        </TableCell>
      ))}
      {canOpenRoutes && (
        <TableCell align="center">
          {route.is_test_mode && (
            // The span keeps the hint reachable: a disabled button fires no hover events.
            <Tooltip title={openHint}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!route.is_ready}
                  onClick={handleOpenClick}
                >
                  เปิดใช้
                </Button>
              </span>
            </Tooltip>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

function OpenFailureAlert({ failure }: Readonly<{ failure: RouteOpenFailure }>) {
  return (
    <Alert severity="error" sx={{ mt: 2 }}>
      {failure.message}
      {failure.blockingItems.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {failure.blockingItems.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </Box>
      )}
    </Alert>
  );
}

interface RouteReadinessTableProps {
  routes: RouteReadiness[];
  /** Master only: the เปิดใช้ column and its confirm dialog. */
  canOpenRoutes: boolean;
  /** Reloads the checklist after an open attempt, whichever way it went. */
  onReadinessChanged: () => void;
}

/** The branch's MAIN routes with their own required items and the เปิดใช้ action. */
export default function RouteReadinessTable({
  routes,
  canOpenRoutes,
  onReadinessChanged,
}: RouteReadinessTableProps) {
  const [pendingRoute, setPendingRoute] = useState<RouteReadiness | null>(null);
  const [opening, setOpening] = useState<boolean>(false);
  const [failure, setFailure] = useState<RouteOpenFailure | null>(null);

  const handleRequestOpen = (route: RouteReadiness) => {
    setFailure(null);
    setPendingRoute(route);
  };

  const handleCloseConfirm = () => {
    if (!opening) setPendingRoute(null);
  };

  const handleConfirmOpen = async () => {
    if (!pendingRoute) return;
    setOpening(true);
    try {
      await openRoute(pendingRoute.route_uuid);
      setPendingRoute(null);
    } catch (error) {
      const response = isAxiosError<BranchErrorResponse<RouteNotReadyError>>(error)
        ? error.response
        : undefined;
      setFailure(resolveRouteOpenFailure(response?.status, response?.data));
    } finally {
      setOpening(false);
      // A refusal means the checklist this click was based on is stale too.
      onReadinessChanged();
    }
  };

  const columnCount = LEADING_COLUMN_COUNT + ROUTE_ITEM_COLUMNS.length + (canOpenRoutes ? 1 : 0);
  const confirmTitle = `เปิดใช้สาย ${pendingRoute?.route_code ?? ""} ${
    pendingRoute?.route_name ?? ""
  }`;

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead sx={{ bgcolor: "background.default" }}>
            <TableRow>
              <TableCell>รหัสสาย</TableCell>
              <TableCell>ชื่อสาย</TableCell>
              <TableCell align="center">สถานะ</TableCell>
              {ROUTE_ITEM_COLUMNS.map(({ key, header }) => (
                <TableCell key={key} align="center">
                  {header}
                </TableCell>
              ))}
              {canOpenRoutes && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {routes.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">ยังไม่มีสายปกติในสาขานี้</Typography>
                </TableCell>
              </TableRow>
            )}
            {routes.map((route) => (
              <RouteRow
                key={route.route_uuid}
                route={route}
                canOpenRoutes={canOpenRoutes}
                onRequestOpen={handleRequestOpen}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmModal
        open={Boolean(pendingRoute)}
        title={confirmTitle}
        description={OPEN_ROUTE_DESCRIPTION}
        confirmLabel="เปิดใช้"
        loading={opening}
        confirmDisabled={Boolean(failure)}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmOpen}
      >
        {failure && <OpenFailureAlert failure={failure} />}
      </ConfirmModal>
    </>
  );
}
